import ProjectsAPI from '../../api/projects';
import AccountCodeManager from '../../managers/AccountCodeManager';
import { AccountCode } from '../../interfaces/AccountCodeInterface';
import { ProjectComplete } from '../../interfaces/ProjectCompleteInterface';
import { ProjectTasksResponse } from '../../interfaces/ProjectTasksInterface';
import {
  CustomerServiceWarrantyOption, getCustomerServiceWarranties,
} from '../../api/customerServiceWarranty';
import {
  OperationalTaskOption, getOperationalTasks,
} from '../../api/operationalTask';

export type { OperationalTaskOption };

/**
 * Payroll editor (M2-B) — session-cached lookups for the entry modals. Every
 * picker reads through these so a payroll sitting of dozens of saves fetches
 * each list once. Failed fetches clear the cache entry so a retry can succeed.
 */

let _accountCodes: AccountCode[] | null = null;
export async function getAccountCodesCached(): Promise<AccountCode[]> {
  if (_accountCodes) return _accountCodes;
  const codes = await AccountCodeManager.getAccountCodes();
  _accountCodes = (codes ?? []).filter((c) => c.active !== false);
  return _accountCodes;
}

let _csw: CustomerServiceWarrantyOption[] | null = null;
export async function getCustomerServiceWarrantiesCached(): Promise<CustomerServiceWarrantyOption[]> {
  if (_csw) return _csw;
  _csw = await getCustomerServiceWarranties();
  return _csw;
}

let _operationalTasks: OperationalTaskOption[] | null = null;
export async function getOperationalTasksCached(): Promise<OperationalTaskOption[]> {
  if (_operationalTasks) return _operationalTasks;
  _operationalTasks = await getOperationalTasks();
  return _operationalTasks;
}

const _completeProjects = new Map<number, ProjectComplete>();
export async function getCompleteProjectCached(projectID: number): Promise<ProjectComplete> {
  const cached = _completeProjects.get(projectID);
  if (cached) return cached;
  const response = await ProjectsAPI.getCompleteProject(projectID);
  const complete = response.data as ProjectComplete;
  _completeProjects.set(projectID, complete);
  return complete;
}

/**
 * The task-claim picker's catalog — `GET /admin/projects/:id/tasks`, one call
 * per project per sitting. Kept apart from `_completeProjects` above because it
 * has a different lifetime: `remainingAmount` goes stale the moment a claim is
 * saved, so the modal calls `invalidateProjectTasks` after every successful
 * save (the phases/account codes in `/complete` don't move that way).
 */
const _projectTasks = new Map<number, ProjectTasksResponse>();

export async function getProjectTasksCached(projectID: number): Promise<ProjectTasksResponse> {
  const cached = _projectTasks.get(projectID);
  if (cached) return cached;
  // A failed fetch must not poison the cache: nothing is stored until it lands,
  // so the picker's "Try again" re-issues the request instead of re-reading a hole.
  const catalog = await ProjectsAPI.getProjectTasks(projectID);
  _projectTasks.set(projectID, catalog);
  return catalog;
}

/** Drop one project's tasks (after a save), or the whole cache when omitted. */
export function invalidateProjectTasks(projectID?: number): void {
  if (projectID == null) _projectTasks.clear();
  else _projectTasks.delete(projectID);
}
